// Session = Firebase user + custom claims {tallerId, rol}. Claims are the
// only source of authorization (the rules enforce them too); the UI uses them
// to hide what the user cannot do, never as the security boundary.
import { rol as rolSchema, type Rol } from '@taller/shared';
import { onIdTokenChanged, signOut, type User } from 'firebase/auth';
import { createContext, use, useSyncExternalStore, type ReactNode } from 'react';
import { auth } from './firebase';

export type Sesion =
  | { estado: 'cargando' }
  | { estado: 'anonima' }
  | {
      estado: 'activa';
      usuario: User;
      uid: string;
      nombre: string;
      email: string | null;
      rol: Rol;
      /** Null only for the platform superadmin. */
      tallerId: string | null;
      /** Still on the temporary password an admin gave: must replace it first. */
      debeCambiarPassword: boolean;
    }
  /**
   * Signed in but without valid claims (e.g. user not provisioned yet), or a
   * shared device account (`equipo`), which only works in the tablet app.
   */
  | { estado: 'sin_permisos'; usuario: User; equipo?: boolean };

export type SesionActiva = Extract<Sesion, { estado: 'activa' }>;

export async function leerSesion(usuario: User | null, forzar = false): Promise<Sesion> {
  if (!usuario) return { estado: 'anonima' };
  const token = await usuario.getIdTokenResult(forzar);
  const rol = rolSchema.safeParse(token.claims['rol']);
  const tallerId = token.claims['tallerId'];
  if (!rol.success) return { estado: 'sin_permisos', usuario };
  if (rol.data === 'equipo') return { estado: 'sin_permisos', usuario, equipo: true };
  if (rol.data !== 'superadmin' && typeof tallerId !== 'string') {
    return { estado: 'sin_permisos', usuario };
  }
  return {
    estado: 'activa',
    usuario,
    uid: usuario.uid,
    nombre: usuario.displayName ?? usuario.email?.split('@')[0] ?? 'Usuario',
    email: usuario.email,
    rol: rol.data,
    tallerId: typeof tallerId === 'string' ? tallerId : null,
    debeCambiarPassword: token.claims['debeCambiarPassword'] === true,
  };
}

// The router reads the session outside React (beforeLoad guards), so the
// latest value also lives in a module-level store.
let actual: Sesion = { estado: 'cargando' };
const oyentes = new Set<(s: Sesion) => void>();
let listo: Promise<Sesion> | null = null;

/** Publish a freshly read session (e.g. right after signing in). */
export function publicar(s: Sesion) {
  actual = s;
  for (const o of oyentes) o(s);
}

/** Resolves once Firebase Auth has restored (or not) the persisted user. */
export async function esperarSesion(): Promise<Sesion> {
  if (!listo) {
    listo = new Promise((resolve) => {
      onIdTokenChanged(auth, (u) => {
        void leerSesion(u).then((s) => {
          publicar(s);
          resolve(s);
        });
      });
    });
  }
  const primera = await listo;
  // After the first resolution, the store holds the latest value (sign-out,
  // token refresh with new claims...), not the first one.
  return actual.estado === 'cargando' ? primera : actual;
}

export function sesionActual(): Sesion {
  return actual;
}

export function cerrarSesion() {
  return signOut(auth);
}

const Contexto = createContext<Sesion>({ estado: 'cargando' });

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const sesion = useSyncExternalStore(
    (cb) => {
      oyentes.add(cb);
      void esperarSesion();
      return () => oyentes.delete(cb);
    },
    () => actual,
  );
  return <Contexto value={sesion}>{children}</Contexto>;
}

export function useSesion(): Sesion {
  return use(Contexto);
}

/** For components rendered only under the authenticated layout. */
export function useSesionActiva(): SesionActiva {
  const s = use(Contexto);
  if (s.estado !== 'activa') throw new Error('useSesionActiva fuera de una ruta protegida');
  return s;
}

export function useTallerId(): string {
  const s = useSesionActiva();
  if (!s.tallerId) throw new Error('Sesión sin taller');
  return s.tallerId;
}

// ---- Permissions (mirror of firestore.rules; the rules are the real gate) ----

export const permisos = {
  moverAutos: (r: Rol) => r === 'jefe_taller' || r === 'admin',
  editarRepuestos: (r: Rol) => r === 'jefe_taller' || r === 'admin',
  avisar: (r: Rol) => r === 'jefe_taller' || r === 'admin',
  revisar: (r: Rol) => r === 'jefe_taller' || r === 'admin',
  anular: (r: Rol) => r === 'admin',
  administrar: (r: Rol) => r === 'admin',
  plataforma: (r: Rol) => r === 'superadmin',
} as const;

export const nombreRol: Record<Rol, string> = {
  recepcion: 'Recepción',
  jefe_taller: 'Jefe de taller',
  admin: 'Administrador',
  superadmin: 'Plataforma',
  equipo: 'Equipo compartido',
};
